import { useState, useRef, useEffect, useCallback } from "react";
import { uploadFiles, fetchVersions, deleteVersion, downloadFile, VersionGroup, FileInfo } from "../services/api";

export default function Files() {
  const [tab, setTab] = useState<"subir" | "historial">("subir");
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadVersion, setUploadVersion] = useState<number | null>(null);
  const [error, setError] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const [versions, setVersions] = useState<VersionGroup[]>([]);
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [deleting, setDeleting] = useState<number>(0);
  const [downloading, setDownloading] = useState("");
  const [downloadingVersion, setDownloadingVersion] = useState(0);

  const loadFiles = useCallback(async () => {
    setLoadingFiles(true);
    try { setVersions(await fetchVersions()); } catch {}
    setLoadingFiles(false);
  }, []);

  useEffect(() => { if (tab === "historial") loadFiles(); }, [tab, loadFiles]);

  function handleFileSelect(e: React.ChangeEvent<HTMLInputElement>) {
    if (e.target.files) setFiles(Array.from(e.target.files));
  }

  function removeFile(i: number) { setFiles((p) => p.filter((_, idx) => idx !== i)); }

  function getFileTypes(fs: { filename: string }[]) {
    const c: Record<string, number> = {};
    for (const f of fs) { const ext = f.filename.split(".").pop()?.toLowerCase() || "?"; c[ext] = (c[ext] || 0) + 1; }
    return Object.entries(c).map(([e, n]) => `${n} .${e}`).join(", ");
  }

  function fmtBytes(b: number) { return b < 1024 ? b + " B" : (b / 1024).toFixed(1) + " KB"; }
  function fmtDate(ts: number) { return new Date(ts).toLocaleDateString("es-MX", { year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(""); setUploadVersion(null);
    if (files.length === 0) { setError("Selecciona al menos un archivo"); return; }
    setUploading(true);
    const ctrl = new AbortController();
    const t = setTimeout(() => ctrl.abort(), 120000);
    try {
      const r = await uploadFiles(files, description, ctrl.signal);
      setUploadVersion(r[0]?.version || 0); setFiles([]); setDescription("");
      if (inputRef.current) inputRef.current.value = "";
    } catch (err) { setError(err instanceof Error ? err.message : "Error al subir"); }
    clearTimeout(t); setUploading(false);
  }

  async function handleDelete(v: number) {
    if (!confirm(`Eliminar toda la Version ${v}?`)) return;
    setDeleting(v);
    try { await deleteVersion(v); setVersions((p) => p.filter((x) => x.version !== v)); }
    catch (err) { alert("Error: " + (err instanceof Error ? err.message : "Error")); }
    setDeleting(0);
  }

  async function handleDownload(f: FileInfo) {
    setDownloading(f.id);
    try { await downloadFile(f.id, f.filename); }
    catch (err) { alert("Error: " + (err instanceof Error ? err.message : "Error al descargar")); }
    setDownloading("");
  }

  async function handleDownloadVersion(v: VersionGroup) {
    if (v.files.length === 0) return;
    if (!confirm(`Descargar los ${v.files.length} archivo(s) de la Version ${v.version}?`)) return;
    setDownloadingVersion(v.version);
    for (const f of v.files) {
      try { await downloadFile(f.id, f.filename); } catch {}
      await new Promise((r) => setTimeout(r, 500));
    }
    setDownloadingVersion(0);
  }

  return (
    <div>
      <h1 className="text-2xl font-bold text-zinc-100 mb-6">Archivos</h1>

      <div className="flex gap-2 mb-6">
        {(["subir", "historial"] as const).map((t) => (
          <button key={t} onClick={() => setTab(t)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              tab === t ? "bg-blue-600 text-white" : "bg-zinc-800 text-zinc-400 hover:text-zinc-200"
            }`}>
            {t === "subir" ? "Subir archivos" : "Historial"}
          </button>
        ))}
      </div>

      {tab === "subir" && (
        <form onSubmit={handleSubmit} className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 space-y-5">
          <div>
            <label className="block text-sm font-medium text-zinc-400 mb-1.5">Comentario / descripcion</label>
            <input type="text" value={description} onChange={(e) => setDescription(e.target.value)}
              placeholder="Ej: Actualizacion de precios junio 2026" disabled={uploading}
              className="w-full bg-zinc-800 border border-zinc-700 rounded-lg px-3 py-2.5 text-sm text-zinc-100 placeholder-zinc-600 focus:outline-none focus:ring-2 focus:ring-blue-600 focus:border-transparent transition" />
          </div>

          <div>
            <label className="block text-sm font-medium text-zinc-400 mb-1.5">Archivos .dbf / .zip / .rar</label>
            <input ref={inputRef} type="file" multiple accept=".dbf,.zip,.rar" onChange={handleFileSelect} disabled={uploading}
              className="block w-full text-sm text-zinc-400 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-blue-600 file:text-white hover:file:bg-blue-700 file:cursor-pointer file:transition-colors" />
            {files.length > 0 && (
              <div className="mt-3 bg-zinc-800 rounded-lg p-3 space-y-1">
                {files.map((f, i) => (
                  <div key={i} className="flex items-center justify-between text-sm">
                    <span className="text-zinc-300">{f.name} <span className="text-zinc-600">({(f.size / 1024).toFixed(1)} KB)</span></span>
                    <button type="button" onClick={() => removeFile(i)} className="text-zinc-600 hover:text-red-400 transition-colors ml-3">&times;</button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <p className="text-xs text-zinc-600">Se creara una nueva version disponible para todas las coneras.</p>

          {error && <div className="bg-red-500/10 border border-red-500/20 rounded-lg px-3 py-2 text-sm text-red-400">{error}</div>}

          <button type="submit" disabled={uploading}
            className="bg-emerald-600 hover:bg-emerald-700 disabled:bg-emerald-600/50 text-white font-medium px-6 py-2.5 rounded-lg text-sm transition-colors disabled:cursor-not-allowed">
            {uploading ? "Subiendo..." : "Subir archivos"}
          </button>

          {uploadVersion !== null && (
            <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-4">
              <p className="text-emerald-400 font-medium">Subida completada — Version {uploadVersion}</p>
            </div>
          )}
        </form>
      )}

      {tab === "historial" && (
        <div>
          {loadingFiles ? (
            <div className="text-zinc-500 py-8 text-center">Cargando...</div>
          ) : versions.length === 0 ? (
            <p className="text-zinc-600 py-8 text-center">No hay archivos subidos.</p>
          ) : (
            <div className="space-y-4">
              {versions.map((v) => (
                <div key={v.version} className="bg-zinc-900 border border-zinc-800 rounded-xl overflow-hidden">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-zinc-800">
                    <div>
                      <span className="font-semibold text-zinc-100">Version {v.version}</span>
                      {v.description && <span className="text-zinc-500 ml-2">— {v.description}</span>}
                      <p className="text-xs text-zinc-600 mt-0.5">{fmtDate(v.uploaded_at)} · {v.files.length} archivos · {getFileTypes(v.files)} · {fmtBytes(v.files.reduce((s, f) => s + f.size, 0))}</p>
                    </div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => handleDownloadVersion(v)} disabled={downloadingVersion === v.version}
                        className="text-xs bg-emerald-500/10 text-emerald-400 hover:bg-emerald-500/20 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50">
                        {downloadingVersion === v.version ? "Descargando..." : "Descargar todo"}
                      </button>
                      <button onClick={() => handleDelete(v.version)} disabled={deleting === v.version}
                        className="text-xs bg-red-500/10 text-red-400 hover:bg-red-500/20 px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50">
                        {deleting === v.version ? "..." : "Eliminar"}
                      </button>
                    </div>
                  </div>
                  <div className="divide-y divide-zinc-800/50">
                    {v.files.map((f) => (
                      <div key={f.id} className="flex items-center justify-between px-4 py-2 text-sm">
                        <span className="text-zinc-300">{f.filename}</span>
                        <div className="flex items-center gap-3">
                          <span className="text-zinc-600">{fmtBytes(f.size)}</span>
                          <button onClick={() => handleDownload(f)} disabled={downloading === f.id}
                            className="text-xs bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 px-3 py-1 rounded-lg transition-colors disabled:opacity-50">
                            {downloading === f.id ? "..." : "Descargar"}
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
